import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom'
import { unitId } from '@/annotations'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { useActiveWorkspaceId, setActiveOrg, setActiveWorkspace } from '@/data/store'
import { CapabilityProvider } from '@/data/capabilities'
import { TopBar } from '@/components/TopBar'
import { Sidebar } from '@/components/Sidebar'
import { VHome } from '@/screens/v-home/VHome'
import { VDashboard } from '@/screens/v-dashboard/VDashboard'
import { VBackup } from '@/screens/v-backup/VBackup'
import { VRestore } from '@/screens/v-restore/VRestore'
import { VJobs } from '@/screens/v-jobs/VJobs'
import { VJobsbackup } from '@/screens/v-jobsbackup/VJobsbackup'
import { VJobsrestore } from '@/screens/v-jobsrestore/VJobsrestore'
import { VBackupjob } from '@/screens/v-backupjob/VBackupjob'
import { VRestorejob } from '@/screens/v-restorejob/VRestorejob'
import { VConnected } from '@/screens/v-connected/VConnected'
import { VSnapshots } from '@/screens/v-snapshots/VSnapshots'
import { VSetbackup } from '@/screens/v-setbackup/VSetbackup'
import { VSetgeneral } from '@/screens/v-setgeneral/VSetgeneral'
import { VSetrepos } from '@/screens/v-setrepos/VSetrepos'
import { VNotify } from '@/screens/v-notify/VNotify'
import { VCredential } from '@/screens/v-credential/VCredential'
import { VDrift } from '@/screens/v-drift/VDrift'
import { VHealth } from '@/screens/v-health/VHealth'
import { VAudit } from '@/screens/v-audit/VAudit'
import { VAuditbb } from '@/screens/v-auditbb/VAuditbb'
import { VAuditprod } from '@/screens/v-auditprod/VAuditprod'
import { VUsers } from '@/screens/v-users/VUsers'
import { VAlerts } from '@/screens/v-alerts/VAlerts'
import { VLostcommits } from '@/screens/v-lostcommits/VLostcommits'
import { VFirstbackup } from '@/screens/v-firstbackup/VFirstbackup'
import { VNotprotected } from '@/screens/v-notprotected/VNotprotected'
import { VPending } from '@/screens/v-pending/VPending'
import { VRemap } from '@/screens/v-remap/VRemap'
import { VBlocked } from '@/screens/v-blocked/VBlocked'
import { VSoon } from '@/screens/v-soon/VSoon'
import { GroupLanding } from '@/screens/shared/GroupLanding'
import { NotYetBuilt } from '@/screens/shared/NotYetBuilt'
import { HOME, NAV_GROUPS } from '@/routes/navigation'
import { resolveCanonicalRouteId } from '@/routes/aliases'
import { RestoreWizardLayer } from '@/layers/restore-wizard/RestoreWizardLayer'
import { REPO_WIZARD_CONFIG } from '@/layers/restore-wizard/configs/repoConfig'
import { ACCESS_WIZARD_CONFIG } from '@/layers/restore-wizard/configs/accessConfig'
import { CI_WIZARD_CONFIG } from '@/layers/restore-wizard/configs/ciConfig'
import { PROJECT_WIZARD_CONFIG } from '@/layers/restore-wizard/configs/projectConfig'
import { REF_WIZARD_CONFIG } from '@/layers/restore-wizard/configs/refConfig'
import { REVIEW_WIZARD_CONFIG } from '@/layers/restore-wizard/configs/reviewConfig'
import { SETTINGS_WIZARD_CONFIG } from '@/layers/restore-wizard/configs/settingsConfig'
import { WSHOOK_WIZARD_CONFIG } from '@/layers/restore-wizard/configs/wshookConfig'
import { ReportLayer } from '@/layers/report/ReportLayer'
import { REPORTS } from '@/layers/report/reportConfig'
import { OnboardingLayer } from '@/layers/onboarding/OnboardingLayer'
import { AuthPageShell } from '@/AuthPageShell'
import { AccountMenu } from '@/layers/shell/AccountMenu'
import type { components } from '@/api/generated/schema'

/** W145's built leaves override `NotYetBuilt` for their nav route. Every other leaf keeps the
 * placeholder until its own pass. Not a generic registry — each screen here takes no props of its
 * own (orgId/workspaceId/capabilities all come from the store/context this shell already
 * provides), which is what lets one lookup replace a per-leaf special case. */
const BUILT_LEAVES: Partial<Record<string, () => ReactElement>> = {
  dashboard: VDashboard,
  backup: VBackup,
  restore: VRestore,
  jobs: VJobs,
  connected: VConnected,
  setbackup: VSetbackup,
  notify: VNotify,
  credential: VCredential,
  drift: VDrift,
  health: VHealth,
  audit: VAudit,
  users: VUsers,
}

type OrganisationMembership = components['schemas']['OrganisationMembership']
type WorkspaceSummary = components['schemas']['WorkspaceSummary']
type Workspace = components['schemas']['Workspace']
type JobRef = components['schemas']['JobRef']

/** `<Route>`'s own `element` can't call `useParams()` itself — this is the one line of
 * indirection that reads `:workspaceId` off the URL and hands it to `OnboardingLayer` as
 * `resumeWorkspaceId`. See `/onboarding/:workspaceId`'s own route comment below for why this
 * route exists at all. */
function ResumeOnboardingRoute({ orgId, onCompleted }: { orgId: string; onCompleted: (workspace: Workspace, started: JobRef | null) => void }) {
  const { workspaceId } = useParams()
  return <OnboardingLayer orgId={orgId} resumeWorkspaceId={workspaceId} onCompleted={onCompleted} />
}

/**
 * `div.shell` → `app-shell` (process/08-COMPONENT-CATALOGUE.md). "The two-region application
 * frame — the sidebar and the main region, and nothing else." Fetches the organisation's
 * capability set (§27.1's ceiling) and this region's workspace list once, provides them to
 * everything it mounts, and routes via `react-router-dom` — `LLD-SPA-027`'s route table, wired
 * for real: every path below comes from `src/routes/navigation.ts`, which is `unitId()`-checked
 * against the generated register at module load, and `LLD-SPA-068`'s "the route is the only
 * thing a reload recovers" falls out of using the browser's own history rather than component
 * state.
 */
export function ShellLayer({
  orgId,
  organisations,
  onChangePassword,
  onSignOut,
}: {
  orgId: string
  organisations: readonly OrganisationMembership[]
  onChangePassword: () => void
  onSignOut: () => void
}) {
  useEffect(() => {
    setActiveOrg(orgId)
  }, [orgId])

  const activeWorkspaceId = useActiveWorkspaceId()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const workspaces = useQuery({
    queryKey: ['workspaces', orgId],
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data.items as unknown as readonly WorkspaceSummary[]
    },
  })

  useEffect(() => {
    if (!activeWorkspaceId && workspaces.data && workspaces.data.length > 0) {
      setActiveWorkspace(workspaces.data[0]!.workspace_id)
    }
  }, [activeWorkspaceId, workspaces.data])

  // `OrgResolver`'s own rule one level up: "an empty list is a real state... the client routes it
  // to the repair rather than rendering an empty application." An org with zero workspaces is the
  // same shape of state — the welcome wizard is mandatory here, full-screen with no TopBar/Sidebar
  // (matching how `AuthLayer` and `OrgResolver` both replace the whole page rather than overlay
  // it), and `ShellChrome` never mounts until the first workspace exists. Adding a SECOND+
  // workspace later still goes through `ShellChrome`'s own in-shell `/onboarding` route below,
  // chrome and all — this gate is only for the very first one.
  //
  // Gated on `isSuccess`, not `isLoading`: an earlier version blanked the screen (`return null`)
  // for the entire loading window, which for the overwhelmingly common case — a returning user who
  // already has a workspace — turned a near-instant dashboard render into a blank page for as long
  // as the fetch took, and a hard blank forever if it errored. Falling through to `ShellChrome`
  // while loading/erroring restores that instant render (workspaces defaults to `[]` until the
  // fetch resolves, same as before this gate existed); the brief chrome flash this can cause for a
  // genuinely brand-new zero-workspace org (one fetch's worth of time) is the acceptable side, not
  // a silently broken dashboard for everyone else.
  if (workspaces.isSuccess && workspaces.data.length === 0) {
    return (
      <AuthPageShell topRight={<AccountMenu onChangePassword={onChangePassword} onSignOut={onSignOut} />}>
        <OnboardingLayer
          orgId={orgId}
          onCompleted={(workspace, started) => {
            queryClient.setQueryData<readonly WorkspaceSummary[]>(['workspaces', orgId], (prev) => [
              ...(prev ?? []),
              workspace as unknown as WorkspaceSummary,
            ])
            setActiveWorkspace(workspace.workspace_id)
            navigate(started ? `/firstbackup/${started.job_id}` : '/dashboard')
          }}
        />
      </AuthPageShell>
    )
  }

  return (
    <ShellChrome
      orgId={orgId}
      organisations={organisations}
      workspaces={workspaces.data ?? []}
      onChangePassword={onChangePassword}
      onSignOut={onSignOut}
    />
  )
}

function ShellChrome({
  orgId,
  organisations,
  workspaces,
  onChangePassword,
  onSignOut,
}: {
  orgId: string
  organisations: readonly OrganisationMembership[]
  workspaces: readonly WorkspaceSummary[]
  onChangePassword: () => void
  onSignOut: () => void
}) {
  const activeWorkspaceId = useActiveWorkspaceId() ?? undefined
  const navigate = useNavigate()
  const location = useLocation()
  const currentRouteId = location.pathname.replace(/^\//, '') || HOME.routeId

  // In-memory only, not persisted — `eslint-rules`' `ui-contract/no-browser-storage` forbids
  // `localStorage` outright (`LLD-SPA-068`: "the route is the only thing a reload may recover").
  // Resets to expanded on a fresh load/reload, same as every other piece of shell state that isn't
  // the route itself.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  // **Real bug, found live W178**: `HOME.routeId` is `'home'`, but its own registered `<Route>`
  // below is `path="/"` (root), not `/home` — `currentRouteId`'s own derivation already treats
  // them as the same thing (`'' || HOME.routeId'` above), but `goTo` had no matching special case,
  // so `onNavigate(HOME.routeId)` (fired by `Sidebar`'s "Bitbucket Command Center" button and by
  // `VHome`'s own leaf links) sent the URL to `/home` — a path with no matching `<Route>` below,
  // rendering a completely blank content area. Every screenshot this session that appeared to show
  // a working home page reached it as the DEFAULT initial route straight after sign-in, never by
  // clicking "Bitbucket Command Center" from another screen — the one path that actually exercises
  // this function on `HOME.routeId` — which is why three separate "it works" verifications missed
  // the exact defect the user then found by clicking through the app like a real user would.
  const goTo = (routeId: string) => {
    const resolved = resolveCanonicalRouteId(routeId)
    navigate(resolved === HOME.routeId ? '/' : `/${resolved}`)
  }

  const queryClient = useQueryClient()

  // `started` is only ever non-null now when `ObWz6.tsx`'s "Start my first backup" actually fired
  // the two `jobTriggerBackup` calls (see that file's own doc comment — `workspaceCompleteOnboarding`
  // itself never launches anything) — so a real job exists to show, and `/jobs/backup` is the right
  // landing page, not `/firstbackup/:jobId` (still a registered route, just no longer linked from
  // here). The invalidate matches `VBackup.tsx`'s own fix for the exact same staleness: without it,
  // landing on the jobs list right after triggering would show whatever was cached from the last
  // visit rather than these new jobs.
  const handleOnboardingCompleted = (workspace: Workspace, started: JobRef | null) => {
    setActiveWorkspace(workspace.workspace_id)
    if (started) {
      queryClient.invalidateQueries({ queryKey: ['backup-jobs', orgId, workspace.workspace_id] })
      navigate('/jobs/backup')
    } else {
      navigate('/dashboard')
    }
  }

  return (
    <CapabilityProvider orgId={orgId}>
      <TopBar
        organisations={organisations}
        currentOrgId={orgId}
        onSwitchOrg={setActiveOrg}
        workspaces={workspaces}
        currentWorkspaceId={activeWorkspaceId}
        onSwitchWorkspace={setActiveWorkspace}
        onNavigate={goTo}
        onChangePassword={onChangePassword}
        onSignOut={onSignOut}
      />
      <Sidebar currentRouteId={currentRouteId} onNavigate={goTo} collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed((v) => !v)} />
      {/* `left-[225px]`/`top-[40px]` mirror `Sidebar`'s own width and the topbar's `h-10` — both
          positioning lengths, exempt from the `--sp-*` token scale per `11-TOKEN-MAP.md`'s "not a
          token" table. `pt-7 pr-8 pb-10 pl-8` is the prototype's own `.main{padding:14px 16px
          26px}` (`--sp-7`/`--sp-8`/`--sp-10`).

          `fixed ... bottom-0 overflow-y-auto` — the SAME shape `Sidebar` already uses
          (`fixed left-0 top-10 bottom-0 ... overflow-y-auto`), and `main` didn't, until now: it
          sat in normal document flow with only a `min-height`, no maximum and no `overflow`, so a
          screen taller than the viewport grew `<body>` itself past the viewport instead of
          scrolling inside its own region — `view-stack`'s own bottom edge (and any bottom action
          bar on it) could end up below the fold, reachable only by scrolling the WHOLE page past
          where `Sidebar`'s own fixed region visually ends. Bounding `main` the same way `Sidebar`
          already is means `view-stack`'s own four edges are always within the viewport; overflow
          content scrolls inside `main` alone.

          `left-16`/`left-[225px]` picked as two complete, static class strings, never a runtime
          `left-[${…}px]` interpolation — Tailwind's build only generates a class for an arbitrary
          value it can find written out literally in the source; an interpolated one silently
          produces no CSS at all rather than an error, which is exactly the failure mode that made
          this the sturdier of the two options. `left-16` is `w-16`'s own value (64px) — `Sidebar`'s
          own collapsed width — off the stock Tailwind scale, not a new value invented here. */}
      <main
        data-scr={unitId('v-section')}
        className={`fixed ${sidebarCollapsed ? 'left-16' : 'left-[225px]'} top-[40px] right-0 bottom-0 overflow-y-auto overflow-x-hidden pt-7 pr-8 pb-10 pl-8`}
      >
        {/* `p-8` is the ONE place this inset is declared — every routed screen used to repeat its
            own `p-8` on its own root div (31 separate copies, all the same value, no shared
            owner), which is exactly the drift risk a "universal padding rule" is meant to close: a
            new screen that forgot the class would render flush against this panel's border with
            no one noticing until a screenshot caught it. Moving it here means a new route gets the
            inset automatically, by construction, rather than by each screen remembering to ask
            for it.

            `min-h-full`, not a `calc(100vh - Npx)` inline style — now that `main` itself is a
            bounded, positioned box (not flow height), 100% of ITS OWN content-box height IS the
            available space below the topbar, so the correct number falls out of the box model
            instead of being a second, independently-maintained copy of `main`'s own height. */}
        <div data-cmp="view-stack" className="bg-panel border border-bd rounded-3 p-8 min-h-full">
          <Routes>
          <Route path="/" element={<VHome onNavigate={goTo} />} />
          {/* Defensive redirect, not the fix itself (that is `goTo`'s own special case above): a
              bookmark, a stale link, or a future regression that sends someone to `/home` directly
              lands on the real home route instead of the blank content area this exact path used
              to render. */}
          <Route path={`/${HOME.routeId}`} element={<Navigate to="/" replace />} />
          {NAV_GROUPS.map((group) => (
            <Route key={group.routeId} path={`/${group.routeId}`} element={<GroupLanding group={group} onNavigate={goTo} />} />
          ))}
          {NAV_GROUPS.flatMap((group) => group.leaves).map((leaf) => {
            const Built = BUILT_LEAVES[leaf.routeId]
            return <Route key={leaf.routeId} path={`/${leaf.routeId}`} element={Built ? <Built /> : <NotYetBuilt unitId={leaf.unitId} title={leaf.title} />} />
          })}
          {/* Not top-level nav leaves — reached by in-app navigation (a launcher card, a row's own
              link) rather than the sidebar, so `src/routes/navigation.ts` does not carry them. */}
          <Route path="/jobs/backup" element={<VJobsbackup />} />
          <Route path="/jobs/backup/:jobId" element={<VBackupjob />} />
          <Route path="/jobs/restore" element={<VJobsrestore />} />
          <Route path="/jobs/restore/:jobId" element={<VRestorejob />} />
          <Route path="/snapshots" element={<VSnapshots />} />
          {/* Pass 7 (W147) — `v-setbackup`'s own two launcher-card destinations; neither is a nav
              leaf, the same "reached by a card, not the sidebar" shape as the Pass 5 job routes. */}
          <Route path="/setgeneral" element={<VSetgeneral />} />
          <Route path="/setrepos" element={<VSetrepos />} />
          {/* Pass 9 (W149) — `v-audit`'s own two launcher-card destinations, the same shape. */}
          <Route path="/auditbb" element={<VAuditbb />} />
          <Route path="/auditprod" element={<VAuditprod />} />
          {/* Pass 10 (W150) — none of these four is a §27.1 nav leaf (confirmed against
              `design/27-ui-surface-and-navigation.md` §27.2's own route table); each is reached by
              an in-app link from a screen this pass does not modify, the same "route exists,
              nothing built yet links to it" shape `firstbackup` and `alerts` are in today. */}
          <Route path="/alerts" element={<VAlerts />} />
          <Route path="/lostcommits/:alertId" element={<VLostcommits />} />
          <Route path="/firstbackup/:jobId" element={<VFirstbackup />} />
          <Route path="/notprotected" element={<VNotprotected />} />
          {/* Pass 11 (W151) — `pending` sits under "a restore job" and `remap` under "Restore"
              per §27.2's own table; neither is a nav leaf. */}
          <Route path="/pending" element={<VPending />} />
          <Route path="/remap" element={<VRemap />} />
          {/* `blocked` is "reached by state rather than by a click" (§27.2's own words) — no
              global ORG_BLOCKED interceptor exists yet to route here automatically (the same
              unmounted-interceptor gap §3.1i's org resolution has carried since W143), so this is
              a real route with no live caller, same as several Pass 10/11 screens. `soon` is
              §27.5 C's own "plumbing and not product" placeholder — not a nav destination, not
              counted in §27.2's screen count, but a real registered unit. */}
          <Route path="/blocked" element={<VBlocked />} />
          <Route path="/soon" element={<VSoon />} />
          {/* Pass 6 (W146) — the eight restore machines. Each is one `RestoreWizardLayer`
              instantiation; only the `-wz-1` entry is a route, the same way `ob-wz-2`…`6` are
              internal to `OnboardingLayer` rather than separately routed. */}
          <Route path="/repo-wz-1" element={<RestoreWizardLayer config={REPO_WIZARD_CONFIG} />} />
          <Route path="/access-wz-1" element={<RestoreWizardLayer config={ACCESS_WIZARD_CONFIG} />} />
          <Route path="/ci-wz-1" element={<RestoreWizardLayer config={CI_WIZARD_CONFIG} />} />
          <Route path="/project-wz-1" element={<RestoreWizardLayer config={PROJECT_WIZARD_CONFIG} />} />
          <Route path="/settings-wz-1" element={<RestoreWizardLayer config={SETTINGS_WIZARD_CONFIG} />} />
          <Route path="/ref-wz-1" element={<RestoreWizardLayer config={REF_WIZARD_CONFIG} />} />
          <Route path="/review-wz-1" element={<RestoreWizardLayer config={REVIEW_WIZARD_CONFIG} />} />
          <Route path="/wshook-wz-1" element={<RestoreWizardLayer config={WSHOOK_WIZARD_CONFIG} />} />
          {/* Pass 8 (W148) — the 21 report screens, one `ReportLayer` instantiation each, reached
              from `v-health`'s own report-row links rather than the sidebar. */}
          {REPORTS.map((r) => (
            <Route key={r.unitId} path={`/reports/${r.unitId}`} element={<ReportLayer unitId={r.unitId} title={r.title} notes={r.notes} />} />
          ))}
          {/* W153 — `OnboardingLayer`'s own first real caller. Its own doc comment has said
              "entered from the dashboard, never from the auth layer" since W142; `v-dashboard`'s
              own spec never carried a control for it (a spec-authoring gap, not a frontend one —
              reported at W150/W151, closed here as a deliberate, flagged exception per Sanket's
              own direction rather than invented silently). Lands on `/jobs/backup` when a capture
              started, or back on the dashboard (with the new workspace made active) when deferred —
              see `handleOnboardingCompleted`'s own comment for why this is `/jobs/backup` and not
              `v-firstbackup` (still a real, registered route below, just no longer linked here). */}
          <Route
            path="/onboarding"
            element={<OnboardingLayer orgId={orgId} onCompleted={handleOnboardingCompleted} />}
          />
          {/* Found live: a workspace stuck between the scope check and `workspaceCompleteOnboarding`
              (`SETUP_UNFINISHED`, `VDashboard`'s own "Finish setup" link) had no route back into this
              wizard at all — `/onboarding` above always restarts stage 1, which dead-ends an already-
              connected workspace on `WORKSPACE_ALREADY_CONNECTED`. `resumeWorkspaceId` re-enters at
              the scope check instead (`OnboardingLayer`'s own doc comment explains why that stage
              specifically, not a guess at ob-wz-5/6). */}
          <Route
            path="/onboarding/:workspaceId"
            element={<ResumeOnboardingRoute orgId={orgId} onCompleted={handleOnboardingCompleted} />}
          />
          {/* §27.4's duplicate-surface aliases (`routes/aliases.ts`'s own `ROUTE_ALIASES`) —
              `settoken`/`drconn` are old prototype ids for screens since merged into
              `credential`/`connected`; a bookmarked or typed URL for either still lands on the
              merged surface. `rrun` is not given a route here — see `aliases.ts`'s own comment. */}
          <Route path="/settoken" element={<Navigate to="/credential" replace />} />
          <Route path="/drconn" element={<Navigate to="/connected" replace />} />
          {/* §27.5 C — "the router's fallback for an unrouted id." Not a nav destination itself
              (no navigation entry ever resolves to it, and it is excluded from §27.2's own route
              count); only reached when a URL names a routeId this table has no `<Route>` for. */}
          <Route path="*" element={<Navigate to="/soon" replace />} />
          </Routes>
        </div>
      </main>
    </CapabilityProvider>
  )
}
